import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Calendar,
  MapPin,
  Plane,
  Building,
  Car,
  Clock,
  User,
  Hash,
  AlertCircle,
  Filter,
  Lock
} from 'lucide-react';
import { useUnfiledComponentEvents } from '../../contexts/SSEContext';

const UnfiledReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    loadUnfiledReservations();
  }, []);

  // Handle SSE events for unfiled components (real-time updates)
  const handleUnfiledComponentEvent = useCallback(() => {
    loadUnfiledReservations();
  }, []);

  useUnfiledComponentEvents(handleUnfiledComponentEvent, []);

  const loadUnfiledReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/unfilled-components');
      setReservations(response.data?.unfilled_components || []);
    } catch (err) {
      setError('Failed to load unfiled reservations: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'flight':
        return <Plane className="w-5 h-5 text-blue-600" />;
      case 'hotel':
        return <Building className="w-5 h-5 text-green-600" />;
      case 'car_rental':
        return <Car className="w-5 h-5 text-purple-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'flight':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'hotel':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'car_rental':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'flight':
        return 'Flight';
      case 'hotel':
        return 'Hotel';
      case 'car_rental':
        return 'Car Rental';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const filteredReservations = selectedType === 'all' 
    ? reservations 
    : reservations.filter(r => r.type === selectedType);

  const groupReservationsByDate = (reservations) => {
    const groups = {};
    
    reservations.forEach(reservation => {
      const date = reservation.date ? new Date(reservation.date) : null;
      const dateKey = date ? date.toDateString() : 'No Date';
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(reservation);
    });
    
    return groups;
  };

  const groupedReservations = groupReservationsByDate(filteredReservations);
  const sortedGroupKeys = Object.keys(groupedReservations).sort((a, b) => {
    if (a === 'No Date') return 1;
    if (b === 'No Date') return -1;
    return new Date(b) - new Date(a);
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unfiled Reservations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Reservations that haven't been linked to any trip yet
          </p>
        </div>
        
        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="flight">Flights</option>
            <option value="hotel">Hotels</option>
            <option value="car_rental">Car Rentals</option>
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Reservations Count */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500">Total Unfiled:</div>
            <div className="text-lg font-semibold text-gray-900">{filteredReservations.length}</div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-600" />
              <span>{reservations.filter(r => r.type === 'flight').length} Flights</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-green-600" />
              <span>{reservations.filter(r => r.type === 'hotel').length} Hotels</span>
            </div>
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-purple-600" />
              <span>{reservations.filter(r => r.type === 'car_rental').length} Car Rentals</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {filteredReservations.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <div className="flex justify-center mb-4">
            <Calendar className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No unfiled reservations</h3>
          <p className="text-gray-500 mb-4">
            {selectedType === 'all' 
              ? 'All your reservations are organized in trips.'
              : `No unfiled ${getTypeLabel(selectedType).toLowerCase()} reservations found.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedGroupKeys.map((dateKey) => (
            <div key={dateKey} className="bg-white shadow rounded-lg overflow-hidden">
              {/* Date Header */}
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <h3 className="font-medium text-gray-900">
                    {dateKey === 'No Date' ? 'No Date Specified' : new Date(dateKey).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                  <span className="text-sm text-gray-500">
                    ({groupedReservations[dateKey].length} reservation{groupedReservations[dateKey].length !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>

              {/* Reservations for this date */}
              <div className="divide-y divide-gray-200">
                {groupedReservations[dateKey].map((reservation) => (
                  <div key={reservation.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {/* Type Icon */}
                        <div className="flex-shrink-0 mt-1">
                          {getTypeIcon(reservation.type)}
                        </div>

                        {/* Reservation Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-lg font-semibold text-gray-900 truncate">
                              {reservation.title}
                            </h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(reservation.type)}`}>
                              {getTypeLabel(reservation.type)}
                            </span>
                            {reservation.is_confidential_inbox && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                <Lock className="h-3 w-3" />
                                Confidential
                              </span>
                            )}
                          </div>

                          {reservation.subtitle && (
                            <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                              <MapPin className="w-3 h-3" />
                              {reservation.subtitle}
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500">
                            {reservation.traveler && (
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                <span className="truncate">{reservation.traveler}</span>
                              </div>
                            )}
                            
                            {reservation.confirmation && (
                              <div className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                <span className="truncate">{reservation.confirmation}</span>
                              </div>
                            )}

                            {reservation.date && (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span className="truncate">{formatDate(reservation.date)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions placeholder - could add "Link to Trip" button here later */}
                      <div className="flex-shrink-0 ml-4">
                        <div className="text-xs text-gray-400">
                          Unfiled
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnfiledReservations;